// Angular Core & Common
import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from "@angular/material/button";

// Translation
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../shared/services/translate/locale-utils';

// Environment & Services
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { GeolocationService } from '../../../shared/services/geolocation/geolocation.service';
import { IdleTimeoutService } from '../../../shared/services/idletimeout/idle-timeout.service';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service';
import { MonitoringService } from '../../../shared/services/monitoring/monitoring-service';
import { SearchStateService } from '../../shared/services/search-state.service';

// Shared Components
import { MychartWebHeaderComponent } from '../../../shared/components/mychart-web-header/mychart-web-header.component';

// Models
import { GetFhirPatientSearchR4Response } from '../../../shared/model/GetFhirPatientSearchR4Response';
import { GetLocationSearchFilterResponse, LocationSearchFilters } from '../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';
import { GetProviderSearchFilterResponse, ProviderSearchFilters } from '../../../shared/model/providerlocationsearch/GetProviderSearchFilterResponse';
import { LocationCoordinates } from '../../../shared/model/LocationCoordinates';
import { PostGetLocationQuickSearchRequest, PostGetProviderQuickSearchRequest } from '../../../shared/model/providerlocationsearch/PostGetProviderQuickSearchRequest';
import { PostGetPatientRegistryAffiliationRequest } from '../../../shared/model/providerlocationsearch/PostGetPatientRegistryAffiliationRequest';

// Web Components
import { LocationSearchComponent } from "../location-search/location-search.component";
import { LocationSearchDetailsComponent } from '../location-search-details/location-search-details.component';
import { LocationSearchFilterComponent } from '../location-search-filter/location-search-filter.component';
import { LocationSearchResultsComponent } from '../location-search-results/location-search-results.component';
import { ProviderSearchComponent } from "../provider-search/provider-search.component";
import { ProviderSearchDetailsComponent } from '../provider-search-details/provider-search-details.component';
import { ProviderSearchFilterComponent } from "../provider-search-filter/provider-search-filter.component";
import { ProviderSearchResultsComponent } from "../provider-search-results/provider-search-results.component";

// Mobile Components
import { LocationSearchComponent as LocationSearchMobile } from '../../mobile/components/location-search/location-search.component';
import { LocationSearchDetailsComponent as LocationSearchDetailsMobile } from '../../mobile/components/location-search-details/location-search-details.component';
import { LocationSearchFilterComponent as LocationSearchFilterMobile } from '../../mobile/components/location-search-filter/location-search-filter.component';
import { LocationSearchResultsComponent as LocationSearchResultsMobile } from '../../mobile/components/location-search-results/location-search-results.component';
import { ProviderSearchComponent as ProviderSearchMobile } from '../../mobile/components/provider-search/provider-search.component';
import { ProviderSearchDetailsComponent as ProviderSearchDetailsMobile } from '../../mobile/components/provider-search-details/provider-search-details.component';
import { ProviderSearchFilterComponent as ProviderSearchFilterMobile } from '../../mobile/components/provider-search-filter/provider-search-filter.component';
import { ProviderSearchResultsComponent as ProviderSearchResultsMobile } from '../../mobile/components/provider-search-results/provider-search-results.component';

@Component({
  selector: 'app-provider-location-search-container-mobile',
  standalone: true,
  imports: [
    CommonModule, TranslateModule, MatButtonModule, MychartWebHeaderComponent, 
    ProviderSearchComponent, ProviderSearchResultsComponent, ProviderSearchFilterComponent, ProviderSearchDetailsComponent, LocationSearchComponent, LocationSearchResultsComponent, LocationSearchFilterComponent, LocationSearchDetailsComponent,
    ProviderSearchMobile, ProviderSearchResultsMobile, ProviderSearchFilterMobile, ProviderSearchDetailsMobile, LocationSearchMobile, LocationSearchResultsMobile, LocationSearchFilterMobile, LocationSearchDetailsMobile
  ],
  templateUrl: './provider-location-search-container.component.html',
  styleUrl: './provider-location-search-container.component.css'
})
export class ProviderLocationSearchContainerComponent {
  geolocation = inject(GeolocationService);
  mcAppGateway = inject(MCAppGatewayService);
  auth: AuthService = inject(AuthService);
  searchState: SearchStateService = inject(SearchStateService);

  @Input() isPlus: boolean | null = null;

  locale: string = '';
  language: string = '';
  currentLocation: LocationCoordinates | null = null;
  isMobileResolution: boolean = false;
  windowResolution: number = 0;

  public title: string = 'Find a Provider';
  public providerBtnText: string = 'Provider';
  public locationBtnText: string = 'Location';
  public eptID: string = '';
  public wprID: string = '';
  public fhirID: string = '';
  public age?: number;
  public gender?: string;
  public patientInfo: GetFhirPatientSearchR4Response | null = null;
  public patientFirstName: string | null = null;
  public patientAffiliation: string | null = null;

  searchMode$ = this.searchState.searchMode$;
  showSpinner$ = this.searchState.showSpinner$;

  providerSearchResults$ = this.searchState.providerSearchResults$;
  providerSearchFilters$ = this.searchState.providerSearchFilters$;
  providerSearchFilterToggle$ = this.searchState.providerSearchFilterToggle$;
  providerSearchTypeAheadOptions$ = this.searchState.providerSearchTypeAheadOptions$;
  providerDetails$ = this.searchState.providerDetails$;
  providerDetailsLocation$ = this.searchState.providerDetailsLocation$;
  providerDetails: any = null;
  fromProviderSearch: boolean = false;
  providerDetailsLocation: any | null = null;

  locationSearchQuery$ = this.searchState.locationSearchQuery$;
  locationSearchResults$ = this.searchState.locationSearchResults$;
  locationSearchFilters$ = this.searchState.locationSearchFilters$;
  locationSearchFilterToggle$ = this.searchState.locationSearchFilterToggle$;
  locationDetails$ = this.searchState.locationDetails$;
  locationDetailsProvider$ = this.searchState.locationDetailsProvider$;
  locationDetails: any = null;
  fromLocationSearch: boolean = false;
  locationDetailsProvider: any | null = null;

  cityTypeAheadOptions$ = this.searchState.cityTypeAheadOptions$;

  public providerQuickSearchOptions: any[] = [];
  public locationQuickSearchOptions: any[] = [];
  public providerSearchFilters: ProviderSearchFilters | null = null;
  public locationSearchFilters: LocationSearchFilters | null = null;
  public providerSearchTypeAheadOptions: string[] = [];
  public locationSearchTypeAheadOptions: string[] = [];
  public currentCityOrZip: string = '';
  public cityTypeAheadOptions: string[] = [];

  constructor(private translate: TranslateService, private idleTimeoutService: IdleTimeoutService, private monitoringService: MonitoringService) {
    this.locale = getLocaleFromUrl();
    sessionStorage.setItem("locale", this.locale);
    this.translate.use(this.locale);
  }

  async ngOnInit() {
    // Retrieve mobile-specific sessionStorage values
    this.patientFirstName = sessionStorage.getItem("patientFirstName") ?? '';
    this.eptID = sessionStorage.getItem("eptID") ?? "";
    this.wprID = sessionStorage.getItem("wprID") ?? "";
    this.fhirID = sessionStorage.getItem("fhirID") ?? "";
    this.age = sessionStorage.getItem("age") ? Number(sessionStorage.getItem("age")) : undefined;
    this.gender = sessionStorage.getItem("gender") || undefined;
    this.isPlus = sessionStorage.getItem("isPlus") === 'true';
    this.patientInfo = JSON.parse(sessionStorage.getItem("patientInfo") || '{}') as GetFhirPatientSearchR4Response;

    // Watch for URL query param changes
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get('mode') as 'provider' | 'location' | null;
    if (modeParam === 'provider' || modeParam === 'location') {
      this.searchState.setSearchMode(modeParam);
    }

    const persistedMode = localStorage.getItem("providerLocationSearchMode") as 'provider' | 'location' || 'provider';
    this.searchState.setSearchMode(persistedMode);

    if (this.locale === 'es-US') {
      this.language = 'spanish';
    }
    else {
      this.language = 'english';
    }

    console.log("Initialized with locale:", this.locale, "language:", this.language, "search mode:", persistedMode);

    this.translate.get('FIND_TITLE').subscribe((translated: string) => {
      this.title = translated;
    });

    this.translate.get('PROVIDER').subscribe((translated: string) => {
      this.providerBtnText = translated;
    });

    this.translate.get('LOCATION').subscribe((translated: string) => {
      this.locationBtnText = translated;
    });

    // Dynamically check if window resolution changes
    this.isMobileResolution = window.innerWidth <= 950;
    window.addEventListener('resize', () => {
      this.isMobileResolution = window.innerWidth <= 950;
    });

    // Listen for screen resolution changes
    this.windowResolution = window.innerWidth;
    window.addEventListener('resize', () => {
      this.windowResolution = window.innerWidth;
    });

    const locationPermissionAllowed = localStorage.getItem('locationPermissionAllowed');
    const storedLocation = localStorage.getItem('currentLocation');

    // If we don't know the user's location permission status, attempt to get location 
    // (which will trigger permission prompt if not already granted/denied)
    if (locationPermissionAllowed === 'false') {
      // User has previously denied location permission, 
      // so we can skip trying to get location and just fetch data without it
      this.getData();
    }
    else if (locationPermissionAllowed === 'true' && storedLocation) {
      try {
        this.currentLocation = JSON.parse(storedLocation) as LocationCoordinates;
      } catch (err) {
        console.error("Failed to parse stored location:", err);
        localStorage.removeItem('currentLocation'); // Clear invalid data
      }
      this.getData(); // Fetch care options immediately, using stored location if available
      
    }
    else {
      const geolocationPromise = this.geolocation.getCurrentLocationAsync();
      const timeoutPromise = new Promise<LocationCoordinates>((_, reject) =>
        setTimeout(() => reject(new Error('Geolocation timeout')), 2000) // 2 seconds timeout
      );

      await Promise.race([geolocationPromise, timeoutPromise])
        .then(location => {
          this.currentLocation = location;  
        })
        .catch(err => {
          console.error("Geolocation failed:", err);
        })
        .finally(() => {
          this.getData();
        });
    }

    this.cityTypeAheadOptions$.subscribe(options => {
      this.cityTypeAheadOptions = options ?? [];
    });

    this.providerSearchFilters$.subscribe(filters => {
      this.providerSearchFilters = filters;
    });

    this.locationSearchFilters$.subscribe(filters => {
      this.locationSearchFilters = filters;

      // Location Type Ahead Options = servicelist + typelist (distinct)
      const serviceValues = filters?.serviceList?.map(service => service.value) ?? [];
      const typeValues = filters?.typeList?.map(type => type.value) ?? [];
      const combinedValues = [...serviceValues, ...typeValues];
      this.locationSearchTypeAheadOptions = [...new Set(combinedValues)];
    });

    this.providerSearchTypeAheadOptions$.subscribe(options => {
      this.providerSearchTypeAheadOptions = options ?? [];
    });

    this.providerDetails$.subscribe(details => {
      this.providerDetails = details;
    });

    this.locationDetails$.subscribe(details => {
      this.locationDetails = details;
    });

    this.providerDetailsLocation$.subscribe(details => {
      this.fromProviderSearch = details != null;
      this.providerDetailsLocation = details;
    });

    this.locationDetailsProvider$.subscribe(details => {
      this.fromLocationSearch = details != null;
      this.locationDetailsProvider = details;
    });

    // Start watching for user inactivity
    this.idleTimeoutService.startWatching(true, this.isPlus);
  }

  getData() {
    // Reverse geocoding to get city from coordinates
    if (this.currentLocation) {
      this.mcAppGateway.PostGetAddressFromCoordinates(this.currentLocation).subscribe({
        next: (response) => {
          console.log('Reverse Geocoding Response:', response);
          this.searchState.setProviderSearchQuery({nameOrSpecialty: '', cityOrZip: response.city});
          this.searchState.setLocationSearchQuery({facilityOrLocationName: '', cityOrZip: response.city});
          this.currentCityOrZip = response.city;
        },
        error: (error) => {
          console.error('Reverse Geocoding Error:', error);
        }
      });
    }

    // get insurance info if authenticated
    if(this.eptID != "" && this.eptID != null) {
      var getAffiliationRequest = new PostGetPatientRegistryAffiliationRequest();
      getAffiliationRequest.patientId = this.eptID

      this.mcAppGateway.PostGetPatientRegistryAffiliation(getAffiliationRequest).subscribe({
        next: (response) => {
          console.log('PostGetRegistryAffiliation API Response:', response);
          this.patientAffiliation = response.Affiliation ?? null;
        },
        error: (error) => {
          console.error('PostGetRegistryAffiliation API Error:', error);
        }
      });
    }

    // Get City Type Ahead Options
    this.mcAppGateway.GetCityTypeAheadOptions().subscribe({
      next: (response: any) => {
        console.log('City Type Ahead Options API Response:', response);

        this.searchState.setCityTypeAheadOptions(response);
      },
      error: (error) => {
        console.error('City Type Ahead Options Error:', error);
      }
    });

    // Get Provider Search Type Ahead Options
    this.mcAppGateway.GetProviderSearchTypeAheadOptions().subscribe({
      next: (response: any) => {
        console.log('Provider Search Type Ahead Options API Response:', response);

        this.searchState.setProviderSearchTypeAheadOptions(response);
      },
      error: (error) => {
        console.error('Provider Search Type Ahead Options Error:', error);
      }
    });

    // Get Provider Quick Search Options
    var provQuickSearchRequest = new PostGetProviderQuickSearchRequest();
    provQuickSearchRequest.language = this.locale;
    provQuickSearchRequest.age = this.age;
    provQuickSearchRequest.gender = this.gender;
    provQuickSearchRequest.maxDisplayCount = 10;

    console.log("Provider Quick Search Request:", provQuickSearchRequest)

    this.mcAppGateway.PostGetProviderQuickSearch(provQuickSearchRequest).subscribe({
      next: (response) => {
        console.log('Provider Quick Search Response:', response);
        this.providerQuickSearchOptions = response;
      },
      error: (error) => {
        console.error('Provider Quick Search Error:', error);
      }
    });

    // Get Provider Search Filters
    this.mcAppGateway.GetProviderSearchFilters().subscribe({
      next: (response: GetProviderSearchFilterResponse) => {
        console.log('Provider Search Filters API Response:', response);

        var filters = new ProviderSearchFilters(response);
        
        // Sort languages: English, Spanish, Korean, Mandarin, Vietnamese first, then rest alphabetically
        const priorityLanguages = ['English', 'Spanish', 'Korean', 'Mandarin', 'Vietnamese'];
        filters.languageList.sort((a, b) => {
          const aIndex = priorityLanguages.indexOf(a.value);
          const bIndex = priorityLanguages.indexOf(b.value);
          
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          return a.value.localeCompare(b.value);
        });

        this.searchState.setProviderSearchFilters(filters);
      },
      error: (error) => {
        console.error('Provider Search Filters Error:', error);
      }
    });

    // Get location quick search options
    var locQuickSearchRequest = new PostGetLocationQuickSearchRequest();
    locQuickSearchRequest.language = this.locale;
    locQuickSearchRequest.age = this.age;
    locQuickSearchRequest.gender = this.gender;
    locQuickSearchRequest.maxDisplayCount = 10;

    this.mcAppGateway.PostGetLocationQuickSearch(locQuickSearchRequest).subscribe({
      next: (response) => {
        console.log('Location Quick Search Response:', response);
        this.locationQuickSearchOptions = response;
      },
      error: (error) => {
        console.error('Location Quick Search Error:', error);
      }
    });

    // Get Location Search Filters
    this.mcAppGateway.GetLocationSearchFilters().subscribe({
      next: (response: GetLocationSearchFilterResponse) => {
        console.log('Location Search Filters API Response:', response);

        var filters = new LocationSearchFilters(response);
        this.searchState.setLocationSearchFilters(filters);
      },
      error: (error) => {
        console.error('Location Search Filters Error:', error);
      }
    });
  }

  toggleSearchMode(mode: 'provider' | 'location') {
    this.searchState.setSearchMode(mode);
    var type = mode.charAt(0).toUpperCase() + mode.slice(1);

    this.searchState.setProviderSearchQuery({ nameOrSpecialty: "", cityOrZip: this.currentCityOrZip })
    this.searchState.clearProviderSearchFilters();
    this.searchState.clearProviderSearchResults();
    this.searchState.clearCurrentProviderDetails();
    this.searchState.clearCurrentLocationDetailsProvider();

    this.searchState.setLocationSearchQuery({ facilityOrLocationName: "", cityOrZip: this.currentCityOrZip })
    this.searchState.clearLocationSearchFilters();
    this.searchState.clearLocationSearchResults();   
    this.searchState.clearCurrentLocationDetails();
    this.searchState.clearCurrentProviderDetailsLocation();
  }

}